import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './WebPlaybackCTA.scss';
import { useToast } from '../contexts/ToastContext';
import { isWeb, isNative, WEB_ONLY_TOAST_MESSAGES } from '../utils/webOnly';

const DOWNLOAD_URL = 'https://mixgoat.com/download';

// Full-card upsell cadence. The card was previously shown once EVER (too rare
// to convert). Now: at most once per session, no more often than every 48h,
// and capped at a few lifetime shows so we stop nagging users who clearly
// aren't converting (the minimized badge stays as a passive reminder).
const CTA_MIN_GAP_MS = 48 * 60 * 60 * 1000; // 48h between full-card shows
const CTA_MAX_SHOWS = 3;                     // give-up cap (lifetime)
const CTA_SHOWN_AT_KEY = 'mixgoat:webPlaybackCTAShownAt';
const CTA_SHOWN_COUNT_KEY = 'mixgoat:webPlaybackCTAShownCount';
const CTA_SESSION_KEY = 'mixgoat:webPlaybackCTAShownSession';

function shouldShowCard() {
    try {
        if (sessionStorage.getItem(CTA_SESSION_KEY) === '1') return false; // once per session
        if (parseInt(localStorage.getItem(CTA_SHOWN_COUNT_KEY) || '0', 10) >= CTA_MAX_SHOWS) return false;
        const lastAt = parseInt(localStorage.getItem(CTA_SHOWN_AT_KEY) || '0', 10);
        return Date.now() - lastAt >= CTA_MIN_GAP_MS;
    } catch { return false; }
}

function recordCardShown() {
    try {
        sessionStorage.setItem(CTA_SESSION_KEY, '1');
        localStorage.setItem(CTA_SHOWN_AT_KEY, String(Date.now()));
        const count = parseInt(localStorage.getItem(CTA_SHOWN_COUNT_KEY) || '0', 10);
        localStorage.setItem(CTA_SHOWN_COUNT_KEY, String(count + 1));
    } catch { /* storage unavailable — just don't persist */ }
}

const WebPlaybackCTA = () => {
    const toast = useToast();
    const user = useSelector((state) => state.auth.user);
    const hasDesktop = !!user?.last_desktop_login_at;
    const [visible, setVisible] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [ctaType, setCtaType] = useState('track');

    useEffect(() => {
        // Native (iOS + Android): never show the "available on the desktop app"
        // upsell (track or mix). Store review — we don't surface a
        // desktop-playback pitch in either store's app. Web keeps it.
        if (!isWeb || isNative) return;

        const handleShow = (e) => {
            if (dismissed) return;
            const type = e.detail?.type || 'track';
            const source = e.detail?.source || 'action';
            // On mobile, skip the 'hint' CTAs (passive nudges when a tab mounts).
            // Real user-triggered actions (track playback, etc.) still surface.
            const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;
            if (isMobileViewport && source === 'hint') return;
            // Desktop users: skip all passive hints — they already know the app.
            if (hasDesktop && source === 'hint') return;
            setCtaType(type);
            // Show the full card on the cadence (once/session, ≥48h apart, capped).
            // Desktop users skip the card entirely — just toast on action.
            if (!hasDesktop && shouldShowCard()) {
                setVisible(true);
                setMinimized(false);
                recordCardShown();
            } else if (source === 'action') {
                const toastMsg = WEB_ONLY_TOAST_MESSAGES[type];
                if (toastMsg) {
                    if (hasDesktop) {
                        toast.info(toastMsg.replace('is only available on the desktop app', 'requires the desktop app'));
                    } else {
                        toast.error(toastMsg);
                    }
                }
                if (minimized) {
                    const badge = document.querySelector('.web-cta-minimized');
                    if (badge) {
                        badge.classList.remove('pulse');
                        void badge.offsetWidth;
                        badge.classList.add('pulse');
                    }
                }
            }
        };

        window.addEventListener('web-playback-cta', handleShow);
        return () => window.removeEventListener('web-playback-cta', handleShow);
    }, [minimized, dismissed, toast, hasDesktop]);

    if (!isWeb || isNative) return null;

    // Minimized state: small badge in bottom-right with X to remove
    if (minimized) {
        return (
            <div className="web-cta-minimized-wrap">
                <button className="web-cta-minimized" onClick={() => { setMinimized(false); setVisible(true); }} title="Full playback info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                </button>
                <button className="web-cta-minimized-close" onClick={() => { setMinimized(false); setDismissed(true); }} title="Dismiss">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>
        );
    }

    if (!visible) return null;

    const messages = {
        track: 'Full track playback is only available on the desktop app. Want the full MixGoat experience?',
        mix: 'Mixes playback is only available on the desktop app. Want the full MixGoat experience?',
        'set-planner': 'In the desktop app, you can build sets from your own collection. Want the full MixGoat experience?',
        'crate-digger': 'In the desktop app, you can find recommendations from your own playlists. Want the full MixGoat experience?',
        feed: 'The artists you already love are dropping sets. Sync on desktop to get a personalized feed of what they play, and what plays alongside your tracks.',
    };
    const message = messages[ctaType] || messages.track;

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    return (
        <div className="web-playback-cta">
            <div className="cta-body">
                <span className="cta-message">{message}</span>
                <div className="cta-actions">
                    <a className="cta-download-btn" href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                        {isMobile ? 'Learn more' : 'Download App'}
                    </a>
                    <button className="cta-minimize-btn" onClick={() => { setVisible(false); setMinimized(true); }}>
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WebPlaybackCTA;
